<template>
    <div class="c-insights-listing">

        <div class="c-insights-listing__header-bar">
            <div class="c-insights-listing__header-bar-layout">
                <div class="c-insights-listing__header-bar-split">
                    <h1 class="c-insights-listing__header-bar-title">
                        {{title}}
                    </h1>
                    <div class="c-insights-listing__search">
                        <span class="c-insights-listing__search-button iconf-search"></span>
                        <input class="c-insights-listing__search-input" name="search" type="text" v-model="searchText" placeholder="Search our insights" />
                    </div>
                </div>
                <div class="c-insights-listing__header-bar-split c-insights-listing__header-bar-split-right">
                    <div class="c-insights-listing__subscribe">
                        <p class="c-insights-listing__subscribe-heading">Get email updates</p>
                        <p class="c-insights-listing__subscribe-text">We offer specialist industry knowledge and the ability to contribute strategic advice in a number of key sectors.</p>
                        <a href="/subscribe" class="c-insights-listing__subscribe-button"><span class="iconf-email"></span>Subscribe to legal updates</a>
                    </div>
                </div>
            </div>
        </div>

        <div class="c-filters-bar c-insights-listing__filter-bar">
            <div class="c-filters__panel c-insights-listing__filters" id="js-filters" v-if="insightsFilters">
                <button type="button"
                        data-flyout-toggle="filter"
                        class="c-filters-toggler mobile-only"
                        aria-label="Show Filters">
                    Show Filters
                </button>

                <filters :filters="insightsFilters"
                         :filter-is-showing="filtersActive"
                         @filters-changed="onFiltersChanged"
                         @toggle-filters="filtersActive = !filtersActive" />
            </div>
        </div>

        <div class="c-insights-listing__wrapper">
            <div class="c-insights-listing__wrapper-layout">

                <div class="c-insights-listing__count c-insights-listing__count--top">
                    {{ displayCount }}
                </div>

                <TransitionGroup class="c-insights-listing__container" name="list" tag="div" mode="out-in" appear>
                    <insight-tile :item="item" v-for="item in paginatedListing" :key="item.id"></insight-tile>
                </TransitionGroup>

                <div class="c-insights-listing__count c-insights-listing__count--bottom">
                    {{ displayCount }}
                </div>

                <pagination v-if="totalItems > pageSize"
                            :total-items="totalItems"
                            :page-size="pageSize"
                            :current-page="currentPage"
                            @page-changed="onPageChanged" />

            </div>
        </div>
    </div>
</template>

<script setup>
    import { ref, computed, watch, onMounted } from 'vue';

    // Reactive state
    const insightsList = ref([]);
    const searchText = ref('');
    const title = ref(null);
    const insightsFilters = ref(null);
    const selectedFilters = ref([]);
    const filtersTouched = ref(false);
    const filtersActive = ref(false);
    const currentPage = ref(1);
    const pageSize = ref(12);
    const totalItems = ref(0);
    const totalPages = ref(0);
    const apiUrl = ref(null);
    const isLoading = ref(false);

    // Computed properties
    const selectedFiltersByGroup = computed(() => {
        const map = {};

        if (!insightsFilters.value || !insightsFilters.value.filters) return map;

        insightsFilters.value.filters.forEach(filter => {
            const selected = (filter.filterOptions || []).filter(opt => opt.selected).map(opt => opt.id);
            if (selected.length) {
                map[filter.name] = selected;
            }
        });

        return map;
    });

    const displayCount = computed(() => {
        if (totalItems.value === 0) {
            return 'No insights found';
        }

        const start = ((currentPage.value - 1) * pageSize.value) + 1;
        const end = Math.min(currentPage.value * pageSize.value, totalItems.value);

        return `Displaying ${start}-${end} of ${totalItems.value}`;
    });

    const filteredListing = computed(() => {
        // With server-side pagination, items are already filtered
        return insightsList.value || [];
    });

    const paginatedListing = computed(() => {
        // With server-side pagination, items are already paginated
        return filteredListing.value;
    });

    // Methods
    const fetchInsights = () => {
        if (!apiUrl.value || isLoading.value) return;

        isLoading.value = true;

        const url = new URL(apiUrl.value);
        url.searchParams.set('page', currentPage.value);

        if (searchText.value) {
            url.searchParams.set('term', searchText.value);
        }

        // Add filter parameters to API request (supports multiple selections)
        const selectedByGroup = selectedFiltersByGroup.value;

        if (selectedByGroup['Expertise'] && selectedByGroup['Expertise'].length > 0) {
            // Send all selected expertise IDs
            selectedByGroup['Expertise'].forEach(id => {
                url.searchParams.append('expertise', id);
            });
        }

        if (selectedByGroup['Author'] && selectedByGroup['Author'].length > 0) {
            // Send all selected author IDs
            selectedByGroup['Author'].forEach(id => {
                url.searchParams.append('author', id);
            });
        }

        if (selectedByGroup['Type'] && selectedByGroup['Type'].length > 0) {
            // Send all selected insight type IDs
            selectedByGroup['Type'].forEach(id => {
                url.searchParams.append('insightType', id);
            });
        }

        if (selectedByGroup['Year'] && selectedByGroup['Year'].length > 0) {
            // Send all selected years
            selectedByGroup['Year'].forEach(id => {
                url.searchParams.append('year', id);
            });
        }

        fetch(url).then(res => res.json()).then(response => {
            insightsList.value = response.items || [];
            insightsFilters.value = response.filters || {};
            pageSize.value = response.pagination?.pageSize || 12;
            totalItems.value = response.pagination?.totalItems || 0;
            totalPages.value = response.pagination?.totalPages || 0;
            currentPage.value = response.pagination?.currentPage || 1;

            // Restore filters from localStorage (only on first load)
            if (!filtersTouched.value) {
                const savedFilters = JSON.parse(localStorage.getItem('insights-listing-filters') || '[]');

                if (savedFilters.length && insightsFilters.value && insightsFilters.value.filters) {
                    insightsFilters.value.filters.forEach(filter => {
                        (filter.filterOptions || []).forEach(option => {
                            option.selected = savedFilters.includes(option.id);
                        });
                    });
                    selectedFilters.value = savedFilters;
                    filtersTouched.value = true;
                }
            } else {
                // Reapply selected state to filter options after fetch
                if (selectedFilters.value.length && insightsFilters.value && insightsFilters.value.filters) {
                    insightsFilters.value.filters.forEach(filter => {
                        (filter.filterOptions || []).forEach(option => {
                            option.selected = selectedFilters.value.includes(option.id);
                        });
                    });
                }
            }

            isLoading.value = false;
        }).catch(() => {
            insightsList.value = [];
            isLoading.value = false;
        });
    };

    const onFiltersChanged = (selected) => {
        selectedFilters.value = selected;
        filtersTouched.value = true;
        currentPage.value = 1; // Reset to first page on filter change

        localStorage.setItem('insights-listing-filters', JSON.stringify(selected));
        fetchInsights();
    };

    const onPageChanged = (page) => {
        currentPage.value = page;
        fetchInsights();

        const offset = 100; // Adjust for sticky header height
        const elementPosition = document.querySelector('.c-insights-listing').getBoundingClientRect().top + window.pageYOffset;

        window.scrollTo({ top: elementPosition - offset, behavior: 'smooth' });
    };

    // Watchers
    watch(searchText, () => {
        currentPage.value = 1; // Reset to first page on search
        fetchInsights();
    });

    // Lifecycle hooks
    onMounted(() => {
        const baseUrl = window.location.origin;

        const DataProp = document.querySelector('.c-insights-listing').parentElement;
        const DataAPI = DataProp?.getAttribute('data-insights-api');
        const DataTitle = DataProp?.getAttribute('data-title');

        title.value = DataTitle;
        apiUrl.value = baseUrl + DataAPI;

        fetchInsights();
    });
</script>